/**
 * Chat Header Component
 * Following PRD Phase 3 specifications (lines 368-372)
 */

import React from 'react';
import { Trash2, Download, MoreVertical, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface ChatHeaderProps {
  onClearChat: () => void;
  onExport: (format: 'json' | 'csv' | 'txt') => void;
  isConnected: boolean;
  messageCount: number;
  tallyConnectionStatus?: 'connected' | 'disconnected' | 'connecting';
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClearChat,
  onExport,
  isConnected,
  messageCount,
  tallyConnectionStatus = 'disconnected'
}) => {
  return (
    <div className="flex items-center justify-between p-4 border-b">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Talk to Genie</h2>

        {/* Chat API Connection status indicator */}
        <div className="flex items-center gap-1.5">
          <Circle
            className={cn(
              "h-2 w-2 fill-current",
              isConnected ? "text-green-500" : "text-red-500"
            )}
          />
          <span className="text-xs text-muted-foreground">
            {isConnected ? 'AccountsGenie Connected' : 'AccountsGenie Connecting...'}
          </span>
        </div>

      </div>

      <div className="flex items-center gap-2">
        {/* Clear chat button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearChat}
          disabled={messageCount === 0}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear
        </Button>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={messageCount === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport('json')}>
              Export as JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('csv')}>
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('txt')}>
              Export as Text
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* More options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled>
              Keyboard Shortcuts
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              View Chat History
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};